import React from 'react'
import { useNavigate } from 'react-router'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { debounce } from 'lodash'
import { BannerStatic } from '../../components'
import { FiltersContainer, SearchFilter } from '../../components/filters'
import { routes } from '../../routes'
import ModuleItem from './module-item'
import { useStyles } from './style'

const SkillsPracticeList = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const [modules, setModules] = React.useState([])
  const [searchResults, setSearchResults] = React.useState(null) // null = not searching
  const initialFilters = React.useMemo(() => ({ search: '' }), [])
  const [filters, setFilters] = React.useState(initialFilters)
  const pollingIntervalRef = React.useRef(null)

  const [getModuleList] = useEvent([
    {
      eventName: 'adminSkillsPracticeList',
      onSuccess: (payload) => {
        setModules(payload?.modules ?? [])
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error loading the Skills Practice modules.', isError: true })
      }
    }
  ])

  const [searchModules] = useEvent([
    {
      eventName: 'adminSkillsPracticeSearch',
      onSuccess: (payload) => {
        setSearchResults(payload?.modules ?? [])
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error searching modules.', isError: true })
      }
    }
  ])

  const refreshList = React.useCallback(() => {
    getModuleList.trigger()
  }, [getModuleList])

  useEventsOnViewLoad(() => {
    getModuleList.trigger()
  }, [getModuleList])

  // Auto-refresh polling for PROCESSING modules.
  // Only start/stop the interval when the processing-presence flag changes,
  // so a successful poll response doesn't kill and recreate the interval.
  const hasProcessing = Array.isArray(modules) && modules.some(m => m.importJobDetails?.status === 'PROCESSING')

  React.useEffect(() => {
    if (hasProcessing && !pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        getModuleList.trigger()
      }, 10000)
    }

    if (!hasProcessing && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [hasProcessing, getModuleList])

  // Unconditional cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [])

  const debouncedSearch = React.useMemo(() => debounce((query) => {
    if (!query) {
      setSearchResults(null)
      return
    }
    searchModules.trigger({ title: query })
  }, 300), [searchModules])

  React.useEffect(() => {
    debouncedSearch(filters.search)
    return () => debouncedSearch.cancel()
  }, [filters.search, debouncedSearch])

  const displayedModules = filters.search ? (searchResults || []) : modules
  const isLoading = getModuleList.isWorking || !getModuleList.hasBeenTriggered
  const isSearching = searchModules.isWorking

  return (
    <>
      <Grid container classes={{ root: classes.header }}>
        <Grid container direction='row' alignItems='center'>
          <Grid item style={{ flexGrow: 1 }}>
            <Typography tabIndex={0} component='div' variant='h5'>
              AI Skills Practice
            </Typography>
          </Grid>
          <Grid item style={{ marginRight: 24 }}>
            <FiltersContainer filters={filters} onChange={setFilters}>
              <SearchFilter name='search' sx={{ background: '#FFF' }} />
            </FiltersContainer>
          </Grid>
          <Grid item>
            <Button
              id='create-skills-practice-module-button'
              data-test='create-skills-practice-module-button'
              variant='contained'
              color='primary'
              onClick={() => navigate(routes.skillsPracticeNew.path)}
            >
              New Module
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Paper classes={{ root: classes.tablePaper }} tabIndex={0} aria-label='Skills Practice Module List' elevation={0}>
        <Grid
          container
          tabIndex={0}
          direction='row'
          alignItems='center'
          className={classes.gridHeader}
          classes={{ root: classes.tableHead }}
          aria-label='List headers'
        >
          <Grid item md={5} sx={{ paddingLeft: 3 }}>Title</Grid>
          <Grid item md={2}>Status</Grid>
          <Grid item md={2}>Last Update</Grid>
          <Grid item md={3} sx={{ textAlign: 'right', paddingRight: 1.5 }}>Actions</Grid>
        </Grid>

        {(isLoading || isSearching) && (
          <Grid container direction='row' justifyContent='center' align='center' classes={{ root: classes.loadingAnimation }}>
            <CircularProgress size={60} color='primary' />
          </Grid>
        )}

        {!isLoading && !isSearching && displayedModules.map((module, index) => (
          <ModuleItem
            key={module.pk || index}
            index={index}
            module={module}
            onDeleted={refreshList}
          />
        ))}

        {!isLoading && !isSearching && displayedModules.length === 0 && (
          <Grid container direction='row' justifyContent='center' align='center' classes={{ root: classes.loadingAnimation }}>
            {filters.search ? (
              <Typography variant='h6'>No modules found for your search.</Typography>
            ) : (
              <Box display='flex' flexDirection='column' alignItems='center' gap={2}>
                <Typography variant='h6'>No modules have been created yet.</Typography>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => navigate(routes.skillsPracticeNew.path)}
                  data-test='create-first-module-button'
                >
                  Create your first module
                </Button>
              </Box>
            )}
          </Grid>
        )}
      </Paper>
    </>
  )
}

export default SkillsPracticeList
